import { motion, useReducedMotion } from "framer-motion";

import {
  VIEWPORT,
  fadeUp,
  respectReducedMotion,
  staggerContainer,
} from "./motionPresets";

/**
 * Container that reveals its <StaggerItem> children one after another.
 *
 * <Stagger className="grid …">
 *   <StaggerItem>…</StaggerItem>
 *   <StaggerItem>…</StaggerItem>
 * </Stagger>
 *
 * The container carries no visual variant of its own — it only owns the timing,
 * so wrapping an existing grid in it does not touch the grid's own styles.
 */
export const Stagger = ({
  as = "div",
  children,
  className,
  stagger = 0.09,
  delayChildren = 0,
  viewport,
  onMount = false,
  ...rest
}) => {
  const shouldReduce = useReducedMotion();
  const Tag = motion[as] || motion.div;

  const animationProps = onMount
    ? { animate: "visible" }
    : { whileInView: "visible", viewport: { ...VIEWPORT, ...viewport } };

  return (
    <Tag
      className={className}
      initial="hidden"
      variants={staggerContainer(shouldReduce ? 0.04 : stagger, delayChildren)}
      {...animationProps}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/**
 * Child of <Stagger>. Inherits `hidden`/`visible` from the parent, so it needs
 * no `whileInView` of its own — that is what keeps the sequence in step.
 */
export const StaggerItem = ({
  as = "div",
  children,
  className,
  y = 26,
  duration,
  variants,
  ...rest
}) => {
  const shouldReduce = useReducedMotion();
  const Tag = motion[as] || motion.div;

  return (
    <Tag
      className={className}
      variants={respectReducedMotion(variants || fadeUp(y, duration), shouldReduce)}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Stagger;
